import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import classnames from 'classnames';
import Truncate from 'react-truncate';

import {
  Progress, DropdownItem, Badge, UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
} from 'reactstrap';
import moment from 'moment';
import {
  getTimeRange, parseAmount, simpleDate, simpleDateThree, parsePaymentStatusText,
} from 'app/utils/parsers';
import Isvg from 'react-inlinesvg';
import history from 'app/routes/history';
import Table from 'app/components/general/Table';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import TickIcon from 'app/images/icons/tick2.svg';
import StatusBadge from 'app/components/general/StatusBadge';

import { getTeamMemberSummary } from 'app/ducks/teamMemberSummaries/selectors';
import { ITeamMemberSummary } from 'app/ducks/teamMemberSummaries/types';
import { getTeamPaymentCollectionSummary } from 'app/ducks/teamPaymentCollections/selectors';
import { ITeamPaymentCollectionSummary } from 'app/ducks/teamPaymentCollections/types';

interface IProps {
  actions: any;
  teamPaymentCollectionsById: any;
  teamPaymentCollectionIds: string[];
  seasonsByIdInSelectFormat: any;
  seasonsById: any;
  seasonIds: string[];
  seasonId?: string;
  currentSeasonId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  reminders?: any;
  createReminder: Function;
  selectTeamMemberSummary: (value) => void;
}

const TeamMemberSummariesTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  teamPaymentCollectionsById: undefined,
  teamPaymentCollectionIds: undefined,
  seasonsById: undefined,
  seasonIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  pagination: undefined,
  reminders: undefined,
  createReminder: undefined,
  selectTeamMemberSummary: undefined,
}) => {
  // const filterOptions = [
  //   {
  //     label: 'Description',
  //     accessor: 'description',
  //     type: 'text',
  //   },
  //   {
  //     label: 'Status',
  //     accessor: 'status',
  //     type: 'select',
  //     options: [
  //       { id: 'owing', name: 'Owing' },
  //       { id: 'overdue', name: 'Overdue' },
  //       { id: 'none', name: 'Up to Date' },
  //     ],
  //   },
  // ];

  const quickFilterOptions = [
    {
      label: 'Season',
      accessor: 'season',
      type: 'select',
      options: props.seasonsByIdInSelectFormat,
      value: _.find(props.seasonsByIdInSelectFormat, (s) => s?.id === props.currentSeasonId),
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllTeamPaymentCollections(payload);
  };

  const handleRequestPayment = (): void => {
    history.push('/teamMemberSummaries/new');
  };

  const handleClick = (id: string): void => {
    // props.actions.selectTeamMemberSummary(id);
    history.push(`/teamMemberSummaries/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.teamPaymentCollectionsById) || props.teamPaymentCollectionIds === undefined) {
      return [];
    }
    const rowsByTeamMemberId = {};
    _.each(props.teamPaymentCollectionIds, (id) => {
      const pc = props.teamPaymentCollectionsById[id];
      const teamMemberId = pc.teamMember.id;
      if (!_.isEmpty(pc)) {
        if (rowsByTeamMemberId[teamMemberId] === undefined) {
          rowsByTeamMemberId[teamMemberId] = {
            id: pc.id,
            seasonId: pc.season?.id,
            teamMemberId: pc.teamMember?.id,
            teamId: pc.team?.id,
            teamMemberName: pc.teamMemberName,
            paymentCollections: [props.teamPaymentCollectionsById[id]],
          };
        } else {
          rowsByTeamMemberId[teamMemberId].paymentCollections.push(props.teamPaymentCollectionsById[id]);
        }
      }
    });
    const rows = _.map(rowsByTeamMemberId, (row) => row);
    return rows;
  }, [props.teamPaymentCollectionsById, props.teamPaymentCollectionIds]);

  const renderTeamMemberName = (cellProps: {
    value: {
      teamMemberName: string;
      teamMemberId: string;
      teamId: string;
    };
  }) => (
    <div
      className="table-cell-clickable w-100 h-100 d-flex align-items-center"
      onClick={(e) => {
        e.stopPropagation();
        history.push(`/team-members/detail/${cellProps.value.teamMemberId}`);
      }}
    >
      {cellProps.value.teamMemberName}
    </div>
  );
  const renderStatusIcon = (paymentText) => {
    if (paymentText === 'Collected') {
      return (
        <span className="text-green">
          <Isvg src={TickIcon} className="icon-small" />
        </span>

      );
    }
    return null;
  };

  const handleCancel = (event, pc): void => {
    event.stopPropagation();
    if (!props.isLoading) {
      if (pc.status === 'pending') {
        props.actions.updateTeamPaymentCollection({
          id: pc.id,
          status: 'cancelled',
        }, () => {
          props.actions.getTeamPaymentRequest(pc.paymentRequestId);
        });
      } else if (pc.status === 'cancelled') {
        props.actions.updateTeamPaymentCollection({
          id: pc.id,
          status: 'pending',
        }, () => {
          props.actions.getTeamPaymentRequest(pc.paymentRequestId);
        });
      }
    }
  };

  const handlePaidOther = (paymentType, event, pc): void => {
    event.stopPropagation();
    if (!props.isLoading) {
      let status = 'pending';
      if (pc.status === 'pending') {
        switch (paymentType) {
          case 'bank':
            status = 'collected-bank-transfer';
            break;
          case 'cash':
            status = 'collected-cash';
            break;
          case 'eftpos':
            status = 'collected-eftpos';
            break;
          case 'other':
            status = 'collected-other';
            break;
          case 'undo':
            status = 'pending';
            break;
          default:
        }
      } else {
        switch (paymentType) {
          case 'undo':
            status = 'pending';
            break;
          default:
        }
      }
      props.actions.updateTeamPaymentCollection({
        id: pc.id,
        status,
      }, () => {
        props.actions.getTeamPaymentRequest(pc.paymentRequestId);
      });
    }
  };

  const renderDropdownOptions = (pc: {
    status: string;
    id: string;
    paymentRequestId: string;
  }): React.ReactNode => {
    const compiledOptions = [];

    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handleCancel(e, pc)}>Cancel request</DropdownItem>);
    }
    if (pc.status === 'cancelled') {
      compiledOptions.push(<DropdownItem onClick={(e) => handleCancel(e, pc)}>Re-activate</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('bank', e, pc)}>Paid (Bank)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('eftpos', e, pc)}>Paid (Eftpos)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('cash', e, pc)}>Paid (Cash)</DropdownItem>);
    }
    if (pc.status === 'pending') {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('other', e, pc)}>Paid (Other)</DropdownItem>);
    }
    if (_.includes(['collected-cash', 'collected-other', 'collected-eftpos', 'collected-bank-transfer'], pc.status)) {
      compiledOptions.push(<DropdownItem onClick={(e) => handlePaidOther('undo', e, pc)}>Undo Payment</DropdownItem>);
    }
    return compiledOptions;
  };

  const renderRoundStatus = (cellProps: {
    value: any;
  }): React.ReactNode => {
    if (cellProps.value === undefined) {
      return null;
    }
    const options = renderDropdownOptions({
      id: cellProps.value.id,
      status: cellProps.value.status,
      paymentRequestId: cellProps.value.paymentRequest?.id,
    });
    const clickable = !_.isEmpty(options);
    return (
      <div className="d-flex flex-wrap align-items-center w-100">
        <UncontrolledDropdown
          direction="down"
          className="d-inline-block"
        >
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownToggle
              tag="div"
              className=""
              data-toggle="dropdown"
            >
              <StatusBadge
                paymentText={cellProps.value.status}
                dueDate={cellProps.value.dueAt}
                amountText={parseAmount(cellProps.value.amount)}
                // amountText={parsePaymentStatusText(cellProps.value.status)}
                tick={false}
                className={classnames('no-margin-right tag-badge', { clickable })}
              />
            </DropdownToggle>
            {clickable && (
              <DropdownMenu positionFixed>
                {options}
              </DropdownMenu>
            )}
          </div>
        </UncontrolledDropdown>
        {/* {renderStatusIcon(parsePaymentStatusText(cellProps.value.status))} */}

        {/* <span className="d-inline-block margin-left-10 text-weight-medium-bold text-black text-medium flex-grow-1">
          {parseAmount(cellProps.value.amount)}
        </span> */}
      </div>
    );
    // return (
    //   <StatusBadge
    //     paymentText={cellProps.value.status}
    //     tick={false}
    //     className="no-margin-right tag-badge"
    //   />
    // );
  };
  const renderRoundStatusHeader = (cellProps: {
    value: {
      description: string;
      date: string;
      paymentRequestId: string;
    };
  }): React.ReactNode => {
    if (cellProps.value === undefined) {
      return null;
    }
    return (
      <div className="table-header-clickable w-100 h-100">
        <span
          className="d-block w-100"
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/payment-requests/detail/${cellProps.value.paymentRequestId}`);
          }}
        >
          <Truncate ellipsis="..">{cellProps.value.description}</Truncate>
          {' '}
          <span className="d-block text-light-grey">
            {simpleDateThree(cellProps.value.date)}
          </span>
        </span>
      </div>
    );
  };

  const tinyColumn = 'col col-3 col-lg-2 col-xl-1 min-width-200';
  const smallColumn = 'col col-3 col-lg-2';
  const growColumn = 'col d-flex';
  const mediumColumn = 'col col-4 col-md-3 col-lg-3 col-xl-2';
  const largeColumn = 'col col-4 col-md-4 col-lg-4 col-xl-3';

  const columns = useMemo(() => {
    let compiledColumns = [
      {
        Header: 'Team Member',
        accessor: (row): any => ({
          teamMemberName: row.teamMemberName,
          teamMemberId: row.teamMemberId,
          teamId: row.teamId,
        }),
        className: mediumColumn,
        headerClassName: mediumColumn,
        Cell: renderTeamMemberName,
        disableSortBy: true,
      },
    ];
    let uniqueDescriptions = _.map(props.teamPaymentCollectionIds, (id) => {
      const pc = props.teamPaymentCollectionsById[id];
      return { description: pc.description, date: pc.dueAt, paymentRequestId: pc.paymentRequest.id };
    });

    uniqueDescriptions = _.uniqBy(uniqueDescriptions, (d) => `${d.description}${d.date}`);
    compiledColumns = _.concat(compiledColumns, _.map(uniqueDescriptions, (desc) => ({
      id: `${desc.description}${desc.date}`,
      accessor: (row): any => {
        const pc = _.find(row.paymentCollections, (o) => desc.description === o.description && o.dueAt === desc.date);
        return pc;
      },
      Header: renderRoundStatusHeader({ value: { description: desc.description, date: desc.date, paymentRequestId: desc.paymentRequestId } }),
      Cell: renderRoundStatus,
      className: tinyColumn,
      headerClassName: tinyColumn,
      disableSortBy: true,
    })));
    if (_.isEmpty(uniqueDescriptions)) {
      compiledColumns = [];
    }
    return compiledColumns;
  }, [JSON.stringify(props.teamPaymentCollectionsById)]);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 team member' : `${props.pagination.meta.totalCount || 0} team members`;

  return (
    <Table
      data={data}
      columns={columns}
      className="sticky-first-column"
      isLoading={props.isLoading}
      aggressiveLoader={false}
      headerTitle={props.headerTitle}
      objectTitle="payment requests"
      filterTitle="Team Members Data"
      // counterText=""
      handleClick={undefined}
      filterable={false}
      saveFilters={props.saveFilters}
      manual={props.manual}
      fetchDataFromApi={fetchDataFromApi}
      // filterOptions={filterOptions}
      quickFilterOptions={quickFilterOptions}
      filterConfig={props.initialFilterConfig}
      persistentFilterConfig={{
        seasonId: props.seasonId,
      }}
      pagination={props.pagination}
      showPagination
      defaultPageSize={1000}
    >
      {/* <Button noLoading onClick={handleRequestPayment} className="btn-thinner btn-2">
        <Isvg src={PlusIcon} className="btn-icon" />
        New Payment Request
      </Button> */}
    </Table>
  );
};

export default TeamMemberSummariesTable;
